import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getEmployeeDashboard } from '../../../services/employee';

const Dashboard = () => {
  const { t } = useTranslation(['common', 'employees']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getEmployeeDashboard();
        setDashboardData(data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden'
      }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:dashboard.title')}
        </Typography>
      </Box>

      {dashboardData && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('employees:dashboard.totalEmployees')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  {dashboardData.total_employees || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('employees:dashboard.activeEmployees')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  {dashboardData.active_employees || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('employees:dashboard.pendingLeaves')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  {dashboardData.pending_leaves || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('employees:dashboard.presentToday')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  {dashboardData.present_today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('employees:dashboard.absentToday')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                  {dashboardData.absent_today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;

